<template>
	<div id="exam">
		<!-- max marks dialog -->
		<v-dialog v-model="dialog" max-width="400" persistent>
			<v-form
				ref="maxMarksForm"
				:disabled="submitLoading"
				@submit.prevent="
					$refs.form.validate()
						? submit().then(() => { dialog = false; })
						: dialog = false;
				"
			>
				<v-card>
					<v-card-title class="justify-center pb-3">
						علامات الاختبارات
					</v-card-title>
					<v-card-text class="pb-1">
						<v-row
							v-for="(item, i) in examSubjectMarks"
							:key="item.examId"
							dense
							align="center"
							:class="{ 'mb-2': i !== examSubjectMarks.length - 1 }"
						>
							<v-col sm="7" cols="12" class="text-body-1 black--text">
								{{examsDueSubject.find(c => c.id === item.examId)?.name}}
							</v-col>
							<v-col sm="5" cols="12">
								<!-- max mark -->
								<v-text-field
									v-if="item.scales && !item.scales.length"
									v-model.number="item.maxMark"
									label="العلامة"
									type="number"
									hide-spin-buttons
									outlined
									dense
									hide-details
									:rules="rules.requiredAsNumber"
								/>

								<!-- max lateral mark -->
								<v-autocomplete
									v-else
									v-model="item.maxMark"
									label="العلامة"
									:items="item.scales"
									item-text="code"
									item-value="orderIndex"
									disabled
									outlined
									dense
									append-icon
									hide-details
									:rules="rules.requiredAsNumber"
								/>
							</v-col>
						</v-row>

						<!-- total -->
						<v-row dense>
							<v-col sm="7" cols="12" />
							<v-col sm="5" cols="12">
								<span
									class="text-start text-body-1 font-weight-bold"
									:class="{ 'error--text': totalSubjectsMark !== mainData.maxMark }"
								>
									{{totalSubjectsMark}}
								</span> / 
								<span class="text-body-2">{{mainData.maxMark}} د</span>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions class="justify-end">
						<v-btn
							type="submit"
							color="primary"
							depressed
							:loading="submitLoading"
							:disabled="totalSubjectsMark !== mainData.maxMark"
						>
							حفظ
						</v-btn>
						<v-btn depressed @click="dialog = false">
							إلغاء
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<v-row no-gutters class="fill-height">
			<!-- sidebar -->
			<v-col lg="2" sm="3" cols="12" class="pt-3 px-3 pb-sm-0 pb-3">
				<div class="font-weight-bold text-h6">
					الخصائص العامة
				</div>

				<!-- data -->
				<div
					class="mt-2 pa-1 overflow-y-auto"
					:style="{ height: `${$vuetify.breakpoint.height - 110}px` }"
				>
					<v-form
						ref="form"
						:disabled="loading || submitLoading"
						@submit.prevent="submit()"
					>
						<v-row dense>
							<!-- exam name -->
							<v-col sm="12" cols="6" class="mb-sm-1">
								<v-text-field
									v-model="mainData.examName"
									label="اسم الاختبار"
									dense
									outlined
									hide-details
									:rules="rules.required"
								/>
							</v-col>

							<!-- class -->
							<v-col sm="12" cols="6" class="mb-sm-1">
								<v-autocomplete
									v-model="mainData.classId"
									label="الصف"
									:items="classes"
									item-text="name"
									item-value="id"
									dense
									outlined
									hide-details
									:rules="rules.requiredAsNumber"
									@change="
										$event
											? (() => {
												fetchAssets();
												mainData.courseId = null;
											})()
											: null
									"
								/>
							</v-col>
							
							<!-- course -->
							<v-col sm="12" cols="6" class="mb-sm-1">
								<v-autocomplete
									v-model="mainData.courseId"
									label="الشعبة"
									:items="courses"
									item-text="title"
									item-value="id"
									:loading="loadingAssets"
									dense
									outlined
									hide-details
									:rules="rules.requiredAsNumber"
								/>
							</v-col>

							<!-- concept -->
							<v-col sm="12" cols="6" class="mb-sm-1">
								<v-autocomplete
									v-model="mainData.conceptId"
									label="Concept"
									:items="concepts"
									item-text="name"
									item-value="id"
									dense
									outlined
									hide-details
									:loading="loadingAssets"
									:rules="rules.requiredAsNumber"
								/>
							</v-col>
						
							<!-- max mark -->
							<v-col sm="12" cols="6" class="mb-sm-1">
								<v-text-field
									v-model.number="mainData.maxMark"
									label="العلامة العظمى"
									type="number"
									hide-spin-buttons
									dense
									outlined
									hide-details
									:rules="rules.numberAsNumber"
								/>
							</v-col>

							<!-- date -->
							<v-col sm="12" cols="6" class="mb-sm-1">
								<v-text-field
									v-model="mainData.date"
									label="التاريخ"
									type="date"
									dense
									outlined
									hide-details
									:rules="rules.required"
								/>
							</v-col>

							<!-- date -->
							<v-col sm="12" cols="6" class="mb-sm-1">
								<v-text-field
									v-model="mainData.time"
									label="الموعد"
									type="time"
									dense
									outlined
									hide-details
									:rules="rules.required"
								/>
							</v-col>

							<!-- subject -->
							<v-col sm="12" cols="6" class="mb-sm-1">
								<span>المحاور</span>
								<v-progress-circular
									v-if="loadingAssets"
									indeterminate
									color="primary"
									size="14"
									width="2"
									class="ms-2"
								/>
								<v-chip-group
									v-else-if="subjectsTree.length"
									v-model="mainData.subjectsIds"
									column
									mandatory
									multiple
									active-class="primary"
								>
									<v-chip
										v-for="(item, i) in subjectsTree"
										:key="i"
										:value="item.id"
										small
										:disabled="loading || submitLoading"
										class="rounded-sm ms-1 px-2"
									>
										{{item.name}}
									</v-chip>
								</v-chip-group>
								<p v-else class="text-caption grey--text text--darken-1 mb-0">
									لا يوجد محاور
								</p>
							</v-col>
						
							<!-- examsIds -->
							<v-col sm="12" cols="6" class="mb-sm-1">
								<span>الاختبارات</span>
								<v-progress-circular
									v-if="loadingAssets"
									indeterminate
									color="primary"
									size="14"
									width="2"
									class="ms-2"
								/>
								<v-chip-group
									v-else-if="examsDueSubject.length"
									v-model="mainData.examsIds"
									column
									mandatory
									multiple
									active-class="primary"
									:disabled="loading || submitLoading"
									@change="updateExamSubjectMarks()"
								>
									<v-chip
										v-for="(item, i) in examsDueSubject"
										:key="i"
										:value="item.id"
										small
										class="rounded-sm ms-1 px-2"
									>
										{{item.name}}
									</v-chip>
								</v-chip-group>
								<p v-else class="text-caption grey--text text--darken-1 mb-0">
									لا يوجد اختبارات
								</p>	
							</v-col>

							<!-- submit -->
							<v-col cols="12">
								<v-btn
									v-if="!$route.params.id"
									depressed
									type="submit"
									width="100%"
									color="primary"
									dense
									:loading="submitLoading"
									:disabled="!mainData.subjectsIds.length || !mainData.examsIds.length"
								>
									<v-icon size="18" class="me-2">$edit</v-icon>
									حفظ البيانات
								</v-btn>
							</v-col>
						</v-row>
					</v-form>
				</div>
			</v-col>
			<v-divider v-if="$vuetify.breakpoint.smAndUp" vertical/>
			
			<!-- content -->
			<v-col lg="8" sm="7" cols="12" :class="{ 'question-content': $vuetify.breakpoint.smAndUp }">
				<div
					class="d-flex flex-wrap flex-sm-row flex-column-reverse justify-space-between align-sm-center px-3 py-2"
				>
					<!-- breadcrumbs -->
					<bee-breadcrumbs
						:items="[
							{
								text: 'الاختبارات',
								disabled: false,
								icon: 'mdi-bookshelf',
								to: { name: 'exams' } },
							{
								text: `${!$route.params.id ? 'إضافة' : 'تعديل'} امتحان`,
								disabled: true
							},
						]"
						class="mt-sm-0 mt-3"
					/>

					<!-- save & back -->
					<div>
						<!-- save -->
						<v-btn
							v-if="$route.params.id"
							depressed
							height="34"
							color="primary"
							class="me-2"
							:disabled="(examHasQuestions ?  totalMark : totalSubjectsMark) !== mainData.maxMark"
							:loading="submitLoading"
							@click="submitQuestions()"
						>
							حفظ الأسئلة
						</v-btn>
						
						<!-- back -->
						<v-btn
							depressed
							height="34"
							:to="{ name: 'exams' }"
						>
							عودة
						</v-btn>
					</div>
				</div>

				<v-divider/>
				<v-progress-linear v-if="loading" height="4" indeterminate color="primary" />

				<!-- questions -->
				<div
					ref="questionsContainer"
					class="pa-3 overflow-y-auto"
					:style="{ height: `${$vuetify.breakpoint.height - 100}px` }"
				>
					<div
						v-if="$route.params.id"
						class="d-flex justify-space-between align-center font-weight-bold"
					>
						<div>
							<span>الأقسام المدرجة</span>
							<span class="mx-3 grey--text">|</span>
							
							<!-- set max marks -->
							<v-tooltip v-if="!examHasQuestions" bottom>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										v-bind="attrs"
										v-on="on"
										:disabled="loading || submitLoading || loadingAssets"
										icon
										width="20"
										height="20"
										:color="totalSubjectsMark === mainData.maxMark ? 'primary' : 'error'"
										class="me-4 pa-0"
										@click="dialog = true"
									>
										<v-icon>mdi-text-box-edit-outline</v-icon>
									</v-btn>
								</template>
								تحديد علامات الاختبارات
							</v-tooltip>

							<!-- auto calculate marks -->
							<v-tooltip v-else bottom>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										v-bind="attrs"
										v-on="on"
										:disabled="loading || submitLoading"
										icon
										width="20"
										height="20"
										color="primary "
										class="me-4 pa-0"
										@click="
											updateQuestionsMarks();
											updateExamSubjectMarks(true);
										"
									>
										<v-icon>mdi-window-shutter-auto</v-icon>
									</v-btn>
								</template>
								توزيع العلامة على الأسئلة
							</v-tooltip>
						</div>
						
						<!-- total mark -->
						<v-tooltip
							bottom
							:disabled="(examHasQuestions ?  totalMark : totalSubjectsMark) === mainData.maxMark"
						>
							<template v-slot:activator="{ on, attrs }">
								<span
									:class="{
										'error--text warn-text':
											!loading &&
											(examHasQuestions ?  totalMark : totalSubjectsMark) !==
												mainData.maxMark
									}"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon
										:color="
											!loading &&
											(examHasQuestions ?  totalMark : totalSubjectsMark) !==
												mainData.maxMark
													? 'error'
													: 'black'
										"
										class="me-1"
									>
										mdi-spellcheck
									</v-icon>
									الدرجة الحالية للاختبار ({{
										(examHasQuestions ?  totalMark : totalSubjectsMark)
									}} د)
								</span>
							</template>
							مجموع العلامات أكبر من العلامة العظمى
						</v-tooltip>
					</div>

					<!-- questions -->
					<div v-if="$route.params.id">
						<div>
							<!-- content order -->
							<div>
								<div
									v-for="(item, index) in contents"
									:key="item.id"
									ref="contentItem"
									class="d-flex mt-3"
									:class="{ 'drag-item': dragIndex === contents.indexOf(item) }"
									:draggable="!loading && !submitLoading"
									@dragstart="
										orderItemId = item.id;
										setDataOnDrag($event, index);
									"
									@dragover.prevent
									@dragend="dragIndex = null"
									@dragenter="dragIndex = index"
									@drop="
										orderItemId !== item.id
										? getDataOnDrop($event, index, contents)
										: null;
									"
								>
									<!-- order icon -->
									<div class="d-flex align-center ps-1 pe-3">
										<v-icon>mdi-menu</v-icon>
									</div>
							
									<!-- content icon -->
									<div class="sub-content-icon d-flex align-center rounded-r px-2">
										<v-icon color="white">
											{{getIconDueType(item.contentType)}}
										</v-icon>
									</div>
							
									<!-- question -->
									<v-card
										v-if="item.contentType === subContentType.question"
										min-height="40"
										flat
										outlined
										class="flex-grow-1 rounded-r-0 pa-2"
									>
										<div class="d-flex align-center">
											<div v-if="item.image">
												<v-img
													:src="item.image"
													class="rounded me-2"
													width="60"
													height="60"
												/>
											</div>
											<div>
												<div
													v-html="item.text || 'نص السؤال'"
													class="question-title font-weight-medium"
												/>
												
												<div class="d-flex align-center text-caption mt-2">
													<div
														v-if="
															item.contentType === questionType.group
																? (item.subContent || [])
																	.some(c => c.type === subContentType.question)
																: true
														"
														class="d-flex align-center me-2"
													>
														<v-icon size="16" class="me-1">mdi-spellcheck</v-icon>
														<input
															v-model.number="item.mark"
															type="number"
															class="mark-input hide-spin-buttons"
															@focus="e => e.target.select()"
														>
													</div>
													<div class="me-2">
														<v-icon size="16" class="me-1">mdi-progress-question</v-icon>
														{{questionTypes.find(c => c.id === item.type)?.name || 'نوع السؤال'}}
													</div>
													<div class="me-2 text-caption">
														<v-icon size="16" class="me-1">
															mdi-card-multiple-outline
														</v-icon>
														{{
															item.type === questionType.single
																? item.options.length
																: (item.subContent || [])
																	.filter(c => c.type === subContentType.question).length
														}}
														{{item.type === questionType.single ? 'خيارات' : 'أسئلة'}}
													</div>
												</div>
											</div>
										</div>
									</v-card>
							
									<!-- divider -->
									<v-card
										v-else-if="item.contentType === subContentType.divider"
										min-height="40"
										flat
										outlined
										class="d-flex align-center flex-grow-1 pa-2 rounded-r-0"
									>
										<hr class="flex-grow-1"/>
									</v-card>
							
									<!-- text -->
									<v-card
										v-else-if="item.contentType === subContentType.text"
										min-height="40"
										flat
										outlined
										class="d-flex align-center flex-grow-1 pa-2 rounded-r-0"
									>
										<v-textarea
											v-model="item.text"
											dir="auto"
											auto-grow
											rows="1"
											hide-details
											placeholder="Write the text here ..."
											class="no-input-border flex-grow-1 pt-0"
										/>
									</v-card>
							
									<!-- image -->
									<v-card
										v-else-if="item.contentType === subContentType.image"
										min-height="40"
										flat
										outlined
										class="d-flex align-center flex-grow-1 pa-2 rounded-r-0"
									>
										<div class="d-flex flex-column">
											<v-img
												:src="item.preview"
												width="80"
												max-width="80"
												height="80"
												max-height="80"
												class="rounded"
											/>
											
											<div class="d-flex flex-wrap align-center text-body-2">
												<div class="me-4 font-weight-bold">إعدادات الصورة</div>
											
												<div class="d-flex align-center">
													<span class="me-2">المحاذاة</span>
													<v-btn-toggle dense active-class="primary">
														<!-- right -->
														<v-btn
															:color="item.imageOptions.align === 'right' ? 'primary' : ''"
															@click="item.imageOptions.align = 'right'"
														>
															<v-icon :color="item.imageOptions.align === 'right' ? 'white' : ''">
																mdi-align-horizontal-right
															</v-icon>
														</v-btn>
														
														<!-- center -->
														<v-btn
															:color="item.imageOptions.align === 'center' ? 'primary' : ''"
															@click="item.imageOptions.align = 'center'"
														>
															<v-icon :color="item.imageOptions.align === 'center' ? 'white' : ''">
																mdi-align-horizontal-center
															</v-icon>
														</v-btn>
														
														<!-- left -->
														<v-btn
															:color="item.imageOptions.align === 'left' ? 'primary' : ''"
															@click="item.imageOptions.align = 'left'"
														>
															<v-icon :color="item.imageOptions.align === 'left' ? 'white' : ''">
																mdi-align-horizontal-left
															</v-icon>
														</v-btn>
													</v-btn-toggle>
												</div>
											
												<div class="d-flex align-center ms-3">
													<span class="me-2">
														الأبعاد
											
														<v-icon>mdi-resize</v-icon>
													</span>

													<div class="d-flex align-center">
														<v-text-field
															v-model.number="item.imageOptions.width"
															label="العرض"
															prefix="px"
															outlined
															dense
															hide-details
															style="max-width: 100px"
															@change="
																item.imageOptions.height =
																	fixNumber(item.imageOptions.width / item.imageOptions.ratio, 0)
															"

														/>
														<span class="mx-2">×</span>
														<v-text-field
															v-model.number="item.imageOptions.height"
															label="الارتفاع"
															prefix="px"
															outlined
															dense
															hide-details
															style="max-width: 100px"
															@change="
																item.imageOptions.width =
																	fixNumber(item.imageOptions.height * item.imageOptions.ratio, 0)
															"
														/>
													</div>
												</div>
											</div>
										</div>
									</v-card>
							
									<!-- remove button -->
									<v-btn
										small
										icon
										tabindex="-1"
										:disabled="loading || submitLoading"
										class="ms-2 pa-1 my-auto"
										@click="removeContent(index)"
									>
										<v-icon size="22">$close</v-icon>
									</v-btn>
								</div>
							</div>
							
							<!-- group actions -->
							<div class="mt-4">
								<v-divider/>
								<p class="font-weight-bold my-3">
									ما الذي ترغب بإضافته للمجموعة؟
								</p>
							
								<div>
									<!-- question -->
									<v-btn
										depressed
										color="primary"
										class="me-3"
										:disabled="loading"
										@click="
											submitQuestions(true);
											addContent(subContentType.question);
										"
									>
										<v-icon class="me-1">
											{{getIconDueType(subContentType.question)}}
										</v-icon>
										سؤال من البنك
									</v-btn>
									
									<!-- divider -->
									<v-btn
										depressed
										color="primary"
										class="me-3"
										:disabled="loading"
										@click="addContent(subContentType.divider)"
									>
										<v-icon class="me-1">
											{{getIconDueType(subContentType.divider)}}
										</v-icon>
										خط فاصل
									</v-btn>
									
									<!-- text -->
									<v-btn
										depressed
										color="primary"
										class="me-3"
										:disabled="loading"
										@click="addContent(subContentType.text)"
									>
										<v-icon class="me-1">
											{{getIconDueType(subContentType.text)}}
										</v-icon>
										نص
									</v-btn>
									
									<!-- image -->
									<v-btn
										depressed
										color="primary"
										:disabled="loading"
										@click="addContent(subContentType.image)"
									>
										<v-icon class="me-1">
											{{getIconDueType(subContentType.image)}}
										</v-icon>
										صورة
									</v-btn>
								</div>
							</div>
						</div>
					</div>

					<!-- please select -->
					<div
						v-else
						class="d-flex flex-column align-center mt-6"
					>
						<v-icon size="40" color="primary" class="mb-4">
							mdi-clipboard-text-multiple-outline
						</v-icon>
						<p class="mb-0 font-weight-medium">
							يرجى إدخال الخصائص العامة أولاً
						</p>
					</div>
				</div>
			</v-col>
			
			<!-- live preview -->
			<v-col sm="2" cols="12" class="pa-2 grey lighten-4">
				<div class="text-end font-weight-bold text-body-2 mb-2">Live preview</div>
				<bee-live-preview
					:questions="contents"
					barcode-text="12345678901234567890123456789012"
				/>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import { questionDifficulties, /* questionDifficulty, */ questionType, questionTypes, subContentType } from '@/helpers/enums';
import { convertFromCmToPx, fixNumber, getDataOnDrop, refactorQuestions, setDataOnDrag } from '@/helpers/functions';
import rules from '@/helpers/validation-rules';
import BeeLivePreview from '@/components/BeeLivePreview.vue';
import { A4Dimensions } from '@/helpers/constants';
import { mapState } from 'vuex';
import moment from 'moment';
export default {
	name: 'Question',
	components: { BeeLivePreview },

	data() {
		return {
			// helpers
			rules,
			questionType,
			questionTypes,
			questionDifficulties,

			loading: false,
			submitLoading: false,
			loadingAssets: false,
			
			courses: [],
			concepts: [],

			expandedPanel: null,
			subContentType,

			orderItemId: null,
			dragIndex: null,

			subjectsTree: [],
			subjectsTree0: [
				{
					id: '1',
					name: 'Reading',
					children: [
						{ id: '1-1', name: 'Reading in phrases', value: 'readingInPhrases' },
						{ id: '1-2', name: 'Pace', value: 'pace' },
						{ id: '1-3', name: 'Syntax', value: 'syntax' },
						{ id: '1-4', name: 'Self-corrections', value: 'selfCorrections' },
						{ id: '1-5', name: 'Intonation', value: 'intonation' },
						{ id: '1-6', name: 'Letter naming fluency', value: 'letterNamingFluency' },
						{ id: '1-7', name: 'Slight word fluency', value: 'slightWordFluency' },
						{ id: '1-8', name: 'Comprehension', value: 'comprehension' },
					]
				},
				{
					id: '2',
					name: 'Phonics awareness',
					children: [
						{ id: '2-1', name: 'Phoneme categorization', value: 'phonemeCategorization' },
						{ id: '2-2', name: 'Phoneme blending', value: 'phonemeBlending' },
						{ id: '2-3', name: 'Phoneme segmentation', value: 'phonemeSegmentation' },
						{ id: '2-4', name: 'Phoneme segmentation', value: 'phonemeSegmentation' },
					],
				},
				{
					id: '3',
					name: 'Readers to writers',
					children: [
						{ id: '3-1', name: 'Spelling', value: 'spelling' },
						{ id: '3-2', name: 'Vocabulary', value: 'vocabulary' },
						{ id: '3-3', name: 'Ideas and content', value: 'ideasAndContent' },
						{ id: '3-4', name: 'Grammar', value: 'grammar' },
						{ id: '3-5', name: 'Structural analysis', value: 'structuralAnalysis' },
						{ id: '3-6', name: 'Writing', value: 'writing' },
					],
				},
				{
					id: '4',
					name: 'Listening and speaking',
					children: [
						{ id: '4-1', name: 'Interactive read aloud', value: 'interactiveReadAloud' },
						{ id: '4-2', name: 'Retelling cards', value: 'retellingCards' },
					],
				},
				{
					id: '5',
					name: 'Social and study skills',
					children: [
						{ id: '5-1', name: 'Participation', value: 'participation' },
						{ id: '5-2', name: 'Completion of work', value: 'completionOfWork' },
						{ id: '5-3', name: 'Concentration', value: 'concentration' },
						{ id: '5-4', name: 'Self direction', value: 'selfDirection' },
						{ id: '5-5', name: 'Cooperation with others', value: 'cooperationWithOthers' },
						{ id: '5-6', name: 'Behavior', value: 'behavior' },
						{ id: '5-7', name: 'Presentation of assignments', value: 'presentationOfAssignments' },
					],
				},
				{
					id: '7',
					name: 'Movement video',
					children: [
						{ id: '7-1', name: 'Movement video', value: 'movementVideo' }
					]
				}
			],

			// exam max marks
			dialog: false,
			examSubjectMarks: [],
			
			// data
			contents: [],
			mainData: {
				name: null,
				conceptId: null,
				classId: null,
				courseId: null,
				maxMark: null,
				subjectsIds: [],
				examsIds: [],
				date: null,
				time: null,
			},
		};
	},

	watch: {
		'examsDueSubject.length'() {
			this.mainData.examsIds =
				this.mainData.examsIds.filter(id => this.examsDueSubject.map(e => e.id).includes(id))
		}
	},

	computed: {
		...mapState({
			classes: state => state.classes.classes,
		}),

		examsDueSubject() {
			return this.subjectsTree
				.filter(c => this.mainData.subjectsIds.includes(c.id))
				.flatMap(c => c.subjectChilds.map(({ scale, ...e }) => ({ ...e, scales: scale })) || [])
		},

		totalMark() {
			return this.contents
				.filter(c => c.contentType === subContentType.question)
				.map(c => c.mark || 0).reduce((a, b) => a + b, 0);
		},

		totalSubjectsMark() {
			return this.examSubjectMarks
				.filter(c => !c.scales || (c.scales && !c.scales.length))
				.map(c => c.maxMark || 0).reduce((a, b) => a + b, 0);
		},

		examHasQuestions() {
			return this.contents.some(c => {
				return c.contentType === subContentType.question && (
					c.type === questionType.single || (
						c.type === questionType.group &&
						c.subContent.some(e => e.type === subContentType.question)
					)
				)
			});
		}
	},

	methods: {
		getIconDueType(type) {
			switch (type) {
			case subContentType.question: return 'mdi-help-circle';
			case subContentType.divider: return 'mdi-slash-forward';
			case subContentType.text: return 'mdi-format-text';
			case subContentType.image: return 'mdi-image-outline';
			}
		},

		submit(isUpdateAll = false) {
			if (this.$refs.form.validate()) {
				this.submitLoading = true;
				const actionName = !this.$route.params.id ? 'create' : 'update';
				return this.$store.dispatch(`exams/${actionName}`, {
					id: this.$route.params.id,
					name: this.mainData.examName,
					conceptId: this.mainData.conceptId,
					courseId: this.mainData.courseId,
					topicTests:
						// this.mainData.examsIds
						this.examSubjectMarks
							.filter(c => this.examsDueSubject.find(e => e.id === c.examId))
							.map(c => ({
								topicTestId: c.examId,
								maxMark: c.maxMark,
								isManual: c.isManual,
							})),
					maxGrade: this.mainData.maxMark,
					dateTime: `${this.mainData.date}T${this.mainData.time}Z`,
				}).then(data => {
					if (!isUpdateAll) {
						if (!this.$route.params.id) {
							this.$router.push({ params: { id: data } });
						}
						this.$snackbar.info('تم تحديث الامتحان بنجاح');
					}
				}).finally(() => {
					if (!isUpdateAll) this.submitLoading = false;
				})
			}
		},

		submitQuestions(isInitBeforeAddFromBank = false) {
			this.submitLoading = true;

			if (this.examHasQuestions) this.updateExamSubjectMarks(true);

			Promise.all([
				this.submit(true),
				this.$store.dispatch('exams/updateQuestions', {
					testId: this.$route.params.id,
					questions: this.contents.map(c => {
						switch (c.contentType) {
						case subContentType.question: return {
							type: subContentType.question,
							questionId: c.questionId,
							degree: c.mark || 0,
						}
						case subContentType.text: {
							return {
								question: {
									type: subContentType.text,
									name: c.text
								}
							}
						}
						case subContentType.image: {
							return {
								question: {
									type: subContentType.image,
									questionImages: [
										{
											id: c.imageId,
											image: c.src || c.preview,
											imageData: ((options) => {
												const data = {
													align: options.align,
													width: options.width,
													height: options.height
												};
												return Object.keys(data)
													.map(key => data[key] ? `${key}:${data[key]}` : '')
													.filter(Boolean)
													.join(';');
											})(c.imageOptions) || ''
										}
									]
								}
							}
						}
						case subContentType.divider: return {
							question: {
								type: subContentType.divider,
								name: 'divider'
							}
						}
						default: return {};
						}
					}).filter(Boolean),
				})
			]).then(() => {
				if (!isInitBeforeAddFromBank) {
					this.$router.replace({ name: 'exams' });
					this.$snackbar.info('تم تحديث أسئلة الامتحان بنجاح')
				}
			}).finally(() => {
				this.submitLoading = false;
			})
		},

		// control page data
		addContent(typeId) {
			switch (typeId) {
			// question
			case subContentType.question: {
				/* this.contents.push({
					id: this.contents.length,
					type: questionType.single,
					contentType: questionType.single,
					text: '',
					image: null,
					options: [{ id: 0, text: '', isCorrect: false }],
					subContent: [],
				}); */
				this.$router.push({
					name: 'questions-bank',
					query: {
						classId: this.mainData.classId || undefined,
						subjectId: this.mainData.examsIds[0] || undefined,
						subjectsIds: this.mainData.subjectsIds || undefined,
						examId: this.$route.params.id,
						examSubjects: this.mainData.examsIds
					}
				});
				break;
			}
			// divider
			case subContentType.divider: {
				this.contents.push({
					id: this.contents.length,
					contentType: subContentType.divider,
				});
				break;
			}
			// text
			case subContentType.text: {
				this.contents.push({
					id: this.contents.length,
					text: '',
					contentType: subContentType.text,
				});
				this.$nextTick(() => {
					this.$refs
						.contentItem[this.contents.length - 1]
						.querySelector('textarea')
						.focus();
				})
				break;
			}
			// image
			case subContentType.image: {
				const imageObj = {
					id: this.contents.length,
					contentType: subContentType.image,
					src: null,
					preview: null,
					imageEl: null,
					imageOptions: {
						align: 'left',
						width: 0,
						height: 0,
						ratio: 0,
					}
				};

				// get image
				const input = document.createElement('input');
				input.type = 'file';
				input.accept = 'image/*';
				input.click();
				input.addEventListener('change', (e) => {
					const file = e.target.files[0];
					imageObj.src = file;
					imageObj.preview = URL.createObjectURL(file);

					imageObj.imageEl = new Image();
					imageObj.imageEl.src = imageObj.preview;
					imageObj.imageEl.onload = () => {
						imageObj.imageOptions.width = imageObj.imageEl.naturalWidth;
						imageObj.imageOptions.height = imageObj.imageEl.naturalHeight;
						imageObj.imageOptions.ratio = imageObj.imageOptions.width / imageObj.imageOptions.height;
						
						const MaxWidthAllowed = fixNumber(convertFromCmToPx(A4Dimensions.CM.WIDTH), 0);
						if (imageObj.imageOptions.width > MaxWidthAllowed) {
							imageObj.imageOptions.width = MaxWidthAllowed;
							imageObj.imageOptions.height = fixNumber(imageObj.imageOptions.width / imageObj.imageOptions.ratio, 0);
						}
					}

					input.remove();
					this.contents.push(imageObj);
				});
				break;
			}
			}

			this.$nextTick(() => {
				this.$refs.questionsContainer.scrollTop = this.$refs.questionsContainer?.scrollHeight;
			})
		},

		removeContent(index) {
			this.contents.splice(index, 1);
			this.contents.forEach((c, i) => 
				c.id > index ? this.contents[i].id-- : null
			)
		},

		updateExamSubjectMarks(useAutomatic = false, initData = []) {
			if (useAutomatic) {
				this.examSubjectMarks =
					this.examSubjectMarks.map(c => ({
						...c,
						maxMark: 0,
						/* this.contents
							.filter(e => c.examId === e.subjectId)
							.reduce((res, a) => res + a.mark, 0), */
						isManual: false
					}));
			} else {
				this.mainData.examsIds.forEach(id => {
					const index = this.examSubjectMarks.findIndex(c => c.examId === id);
					if (index === -1) {
						const data = initData.find(c => c.examId === id);
						const scales =
							(this.examsDueSubject.find(c => c.id === id)?.scales || [])
								.sort((a, b) => a.orderIndex - b.orderIndex);
						
						this.examSubjectMarks.push({
							examId: id,
							maxMark: data ? data.maxMark : scales[scales.length - 1]?.orderIndex || 0,
							isManual: data ? data.isManual : true,
							scales: data ? data.scales : scales
						});
					}
				})
			}

			this.examSubjectMarks =
				this.examSubjectMarks.filter(c => this.mainData.examsIds.includes(c.examId));
		},

		updateQuestionsMarks() {
			const examMark = this.mainData.maxMark;
			
			const questionsCount =
				this.contents
					.filter(c => c.contentType === subContentType.question)
					.reduce((res, a) => {
						if (a.type === questionType.single) {
							res++;
						} else {
							res += a.subContent.filter(c => c.type === subContentType.question).length;
						}
						return res;
					}, 0);

			const questionMark = examMark / (questionsCount || 1);
			
			for (let i = 0; i < this.contents.length; i++) {
				const item = this.contents[i];
				if (item.contentType === subContentType.question) {
					if (item.type === questionType.single) {
						this.contents[i].mark = questionMark;
					} else {
						// group question mark came from (count of sub questions × question mark)
						this.contents[i].mark = 
							item.subContent.filter(c => c.type === subContentType.question).length * questionMark;
					}
				}
			}
		},

		// fetch
		fetchAssets() {
			if (!this.mainData.classId) return;
			this.loadingAssets = true;
			Promise.all([
				this.$store.dispatch('classes/fetchCoursesByClassId', {
					classId: this.mainData.classId
				}).then((data) => {
					this.courses = data;
				}),
				this.$store.dispatch('classes/fetchConceptsByClassId', {
					classId: this.mainData.classId
				}).then((data) => {
					this.concepts = data;
				}),
				this.$store.dispatch('subjects/fetchByClassId', {
					classId: this.mainData.classId
				}).then((data) => {
					this.subjectsTree = data;
				}),
			]).finally(() => {
				this.loadingAssets = false;
			});
		},

		fetchExam() {
			this.loading = true;
			return Promise.all([
				this.$store.dispatch('exams/fetchById', {
					id: this.$route.params.id
				}).then((data) => {
					this.mainData.examName = data.name;
					this.mainData.conceptId = data.concept?.id;
					this.mainData.classId = data.course.specialty.id;
					this.mainData.courseId = data.course.id;
					this.mainData.maxMark = data.maxGrade;
					this.mainData.subjectsIds =
						data.topics.map(c => c.topic.parent.id).filter((c, i, arr) => arr.indexOf(c) === i);
					this.mainData.examsIds = data.topics.map(c => c.topic.id);
					this.mainData.date = moment(data.dateTime).format('YYYY-MM-DD');
					this.mainData.time = moment(data.dateTime).format('HH:mm');

					this.updateExamSubjectMarks(
						false,
						data.topics.map(c => {
							const scales = c.topic.scales.sort((a, b) => a.orderIndex - b.orderIndex); 
							return {
								examId: c.topic.id,
								maxMark:
									!scales.length ? c.topicMaxMark : scales[scales.length - 1].orderIndex,
								isManual: c.isManual,
								scales
							}
						})
					);
				}),
				this.$store.dispatch('exams/fetchQuestions', {
					id: this.$route.params.id,
				}).then(data => {
					this.contents = refactorQuestions(
						data.map(c => ({ ...c.questionBankBriefResponse, degree: c.degree })),
						true
					);
				})
			]).finally(() => {
				this.loading = false;
			});
		},

		// helpers
		setDataOnDrag,
		getDataOnDrop,
		fixNumber
	},

	created() {
		/* eslint-disable quotes, quote-props, indent */
		this.contents = [] || [
			{
				"id": 0,
				"questionId": 1147,
				"subjectId": 397,
				"type": 0,
				"contentType": 0,
				"text": "<p>Single Quetion</p>",
				"image": null,
				"imageId": null,
				"options": [
				{
					"id": 0,
					"text": "1",
					"isCorrect": false
				},
				{
					"id": 1,
					"text": "2",
					"isCorrect": true
				},
				{
					"id": 2,
					"text": "3",
					"isCorrect": false
				},
				{
					"id": 3,
					"text": "4",
					"isCorrect": false
				}
				],
				"mark": 0,
				"subContent": []
			},
			{
				"id": 1,
				"questionId": 1140,
				"subjectId": 397,
				"type": 1,
				"contentType": 0,
				"text": "<p>Some nested Quetions</p>",
				"image": null,
				"imageId": null,
				"options": [],
				"mark": 0,
				"subContent": [
				{
					"id": 0,
					"contentId": 1141,
					"text": "<p>What you need?</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "1",
						"isCorrect": true
					},
					{
						"id": 1,
						"text": "2",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "3",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "4",
						"isCorrect": false
					}
					]
				},
				{
					"id": 1,
					"contentId": 1142,
					"text": "divider",
					"type": 1,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 2,
					"contentId": 1143,
					"text": "<p>Is it good for that?</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "1",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "2",
						"isCorrect": true
					},
					{
						"id": 2,
						"text": "3",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "4",
						"isCorrect": false
					}
					]
				},
				{
					"id": 3,
					"contentId": 1144,
					"text": "Some Test",
					"type": 2,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 4,
					"contentId": 1145,
					"text": "divider",
					"type": 1,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 5,
					"contentId": 1146,
					"text": "<p>What is the best</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "55",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "33",
						"isCorrect": true
					},
					{
						"id": 2,
						"text": "111",
						"isCorrect": false
					}
					]
				}
				]
			},
			{
				"id": 2,
				"questionId": 1147,
				"subjectId": 397,
				"type": 0,
				"contentType": 0,
				"text": "<p>Single Quetion</p>",
				"image": null,
				"imageId": null,
				"options": [
				{
					"id": 0,
					"text": "1",
					"isCorrect": false
				},
				{
					"id": 1,
					"text": "2",
					"isCorrect": true
				},
				{
					"id": 2,
					"text": "3",
					"isCorrect": false
				},
				{
					"id": 3,
					"text": "4",
					"isCorrect": false
				}
				],
				"mark": 0,
				"subContent": []
			},
			{
				"id": 3,
				"questionId": 1140,
				"subjectId": 397,
				"type": 1,
				"contentType": 0,
				"text": "<p>Some nested Quetions</p>",
				"image": null,
				"imageId": null,
				"options": [],
				"mark": 0,
				"subContent": [
				{
					"id": 0,
					"contentId": 1141,
					"text": "<p>What you need?</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "1",
						"isCorrect": true
					},
					{
						"id": 1,
						"text": "2",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "3",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "4",
						"isCorrect": false
					}
					]
				},
				{
					"id": 1,
					"contentId": 1142,
					"text": "divider",
					"type": 1,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 2,
					"contentId": 1143,
					"text": "<p>Is it good for that?</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "1",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "2",
						"isCorrect": true
					},
					{
						"id": 2,
						"text": "3",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "4",
						"isCorrect": false
					}
					]
				},
				{
					"id": 3,
					"contentId": 1144,
					"text": "Some Test",
					"type": 2,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 4,
					"contentId": 1145,
					"text": "divider",
					"type": 1,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 5,
					"contentId": 1146,
					"text": "<p>What is the best</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "55",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "33",
						"isCorrect": true
					},
					{
						"id": 2,
						"text": "111",
						"isCorrect": false
					}
					]
				}
				]
			},
			{
				"id": 4,
				"questionId": 1147,
				"subjectId": 397,
				"type": 0,
				"contentType": 0,
				"text": "<p>Single Quetion</p>",
				"image": null,
				"imageId": null,
				"options": [
				{
					"id": 0,
					"text": "1",
					"isCorrect": false
				},
				{
					"id": 1,
					"text": "2",
					"isCorrect": true
				},
				{
					"id": 2,
					"text": "3",
					"isCorrect": false
				},
				{
					"id": 3,
					"text": "4",
					"isCorrect": false
				}
				],
				"mark": 0,
				"subContent": []
			},
			{
				"id": 5,
				"questionId": 1140,
				"subjectId": 397,
				"type": 1,
				"contentType": 0,
				"text": "<p>Some nested Quetions</p>",
				"image": null,
				"imageId": null,
				"options": [],
				"mark": 0,
				"subContent": [
				{
					"id": 0,
					"contentId": 1141,
					"text": "<p>What you need?</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "1",
						"isCorrect": true
					},
					{
						"id": 1,
						"text": "2",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "3",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "4",
						"isCorrect": false
					}
					]
				},
				{
					"id": 1,
					"contentId": 1142,
					"text": "divider",
					"type": 1,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 2,
					"contentId": 1143,
					"text": "<p>Is it good for that?</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "1",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "2",
						"isCorrect": true
					},
					{
						"id": 2,
						"text": "3",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "4",
						"isCorrect": false
					}
					]
				},
				{
					"id": 3,
					"contentId": 1144,
					"text": "Some Test",
					"type": 2,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 4,
					"contentId": 1145,
					"text": "divider",
					"type": 1,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 5,
					"contentId": 1146,
					"text": "<p>What is the best</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "55",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "33",
						"isCorrect": true
					},
					{
						"id": 2,
						"text": "111",
						"isCorrect": false
					}
					]
				}
				]
			},
			{
				"id": 6,
				"questionId": 1147,
				"subjectId": 397,
				"type": 0,
				"contentType": 0,
				"text": "<p>Single Quetion</p>",
				"image": null,
				"imageId": null,
				"options": [
				{
					"id": 0,
					"text": "1",
					"isCorrect": false
				},
				{
					"id": 1,
					"text": "2",
					"isCorrect": true
				},
				{
					"id": 2,
					"text": "3",
					"isCorrect": false
				},
				{
					"id": 3,
					"text": "4",
					"isCorrect": false
				}
				],
				"mark": 0,
				"subContent": []
			},
			{
				"id": 7,
				"questionId": 1140,
				"subjectId": 397,
				"type": 1,
				"contentType": 0,
				"text": "<p>Some nested Quetions</p>",
				"image": null,
				"imageId": null,
				"options": [],
				"mark": 0,
				"subContent": [
				{
					"id": 0,
					"contentId": 1141,
					"text": "<p>What you need?</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "1",
						"isCorrect": true
					},
					{
						"id": 1,
						"text": "2",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "3",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "4",
						"isCorrect": false
					}
					]
				},
				{
					"id": 1,
					"contentId": 1142,
					"text": "divider",
					"type": 1,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 2,
					"contentId": 1143,
					"text": "<p>Is it good for that?</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "1",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "2",
						"isCorrect": true
					},
					{
						"id": 2,
						"text": "3",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "4",
						"isCorrect": false
					}
					]
				},
				{
					"id": 3,
					"contentId": 1144,
					"text": "Some Test",
					"type": 2,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 4,
					"contentId": 1145,
					"text": "divider",
					"type": 1,
					"image": null,
					"imageId": null,
					"options": []
				},
				{
					"id": 5,
					"contentId": 1146,
					"text": "<p>What is the best</p>",
					"type": 0,
					"image": null,
					"imageId": null,
					"options": [
					{
						"id": 0,
						"text": "55",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "33",
						"isCorrect": true
					},
					{
						"id": 2,
						"text": "111",
						"isCorrect": false
					}
					]
				}
				]
			},
		] || [
			{
				id: 1,
				type: questionType.single,
				contentType: subContentType.question,
				text: '<p>Choose the best answer</p>',
				mark: 50,
				image: 'https://plus.unsplash.com/premium_photo-1664474619075-644dd191935f',
				options: Array.from({ length: 5 }).map((c, i) => ({ id: i, text: `option ${i}` })),
				subContent: []
			},
			{
				id: 2,
				contentType: subContentType.divider,
			},
			{
				id: 3,
				contentType: subContentType.text,
				text: 'some text'
			},
			{
				id: 4,
				contentType: subContentType.image,
				src: 'https://plus.unsplash.com/premium_photo-1664474619075-644dd191935f',
				preview: 'https://plus.unsplash.com/premium_photo-1664474619075-644dd191935f',
				imageEl: (() => {
					const image = new Image();
					image.src = 'https://plus.unsplash.com/premium_photo-1664474619075-644dd191935f';
					return image
				})(),
				imageOptions: {
					align: 'left',
					width: 200,
					height: 100,
					ratio: 2
				}
			},
			{
				id: 5,
				type: questionType.group,
				contentType: subContentType.question,
				text: '<p>Choose the best answer</p>',
				mark: 50,
				image: 'https://plus.unsplash.com/premium_photo-1664474619075-644dd191935f',
				options: [],
				subContent: [
					{
						id: 1,
						type: subContentType.question,
						questionType: questionType.single,
						text: '<p>Choose the best answer</p>',
						mark: 50,
						image: 'https://plus.unsplash.com/premium_photo-1664474619075-644dd191935f',
						options: Array.from({ length: 5 }).map((c, i) => ({ id: i, text: `option ${i}` })),
						subContent: []
					},
					{ id: 2, type: subContentType.divider },
					{ id: 3, type: subContentType.text, text: 'How to test?' },
					{
						id: 5,
						type: subContentType.image,
						src: null,
						preview: 'https://plus.unsplash.com/premium_photo-1664474619075-644dd191935f'
					}
				]
			},
		];

		this.mainData.classId = +this.$route.query.classId;
		this.mainData.courseId = +this.$route.query.courseId;
		this.mainData.conceptId = +this.$route.query.conceptId;
		if (this.$route.params.id) {
			this.fetchExam().finally(() => this.fetchAssets());
		} else {
			this.fetchAssets();
		}
	},
}
</script>

<style lang="scss">
#exam {
	height: 100%;

	.question-content {
		border-left: 1px solid #e0e0e0;
	}

	input.mark-input {
		height: 20px;
		min-width: 40px;
		width: 40px;
		padding: 2px 4px;
		border: 1px solid #d3d3d3;
		border-radius: 6px;
	}

	.warn-text {
		animation: warn-text 1s infinite ease-in-out;

		@keyframes warn-text {
			0% { transform: scale(1); }
			// 25% { transform: translateX(-5px); }
			50% { transform: scale(0.96); }
			// 75% { transform: translateX(-5px); }
			100% { transform: scale(1); }

		}
	}
}
</style>